/**
 * 작성자 : 홍선영
 * 날짜 : 2023.09.04
 * 경로 :  설정관리-협력업체관리-협력업체정보탭(현장관리자) 인풋폼
 */

import { useEffect, Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Input from '../Input';
import { BtnBlue, BtnGreen, BtnRed } from '../Button';
import { useSetAuth } from '../../utils/useSetAuth';
import { applyBorderStyle } from '../../utils/applyBorderStyle';
import DatePicker from '../DatePicker';
import TextEditor from '../TextEditor';
import ClickableContainer from '../../pages/h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from '../TableLamp';
import dayjs from 'dayjs';
import RangePicker, { onChangeRangeInput } from '../RangePicker';

interface IUserInputForm {
  type: 's' | 'c';
  contentKey: 'cContent' | 'cContents';
  state: any;
  setState: Dispatch<SetStateAction<any>>;
  isNewAdd?: boolean;
  userInfoInputFormRef: React.RefObject<any>;
  onClickNewAdd: () => void;
  onClickSave: () => void;
  onClickDelete: () => void;
  onClickPreview: () => void;
  isSaveClicked: boolean;
  date: { start: string; end: string };
  setDate: Dispatch<SetStateAction<{ start: string; end: string }>>;
  cDate: string;
  setCDate: Dispatch<SetStateAction<string>>;
  editorData: string;
  setEditorData: Dispatch<SetStateAction<string>>;
}

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;
const Root = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  overflow: hidden;
  justify-content: flex-start;

  .useYn > div {
    justify-content: flex-start !important;
  }
  .editorWrapper .ck-content {
    height: 16rem;
    /* height: 100%; */
    /* height: 25rem; */
  }
  .seperator {
    margin: 0.5rem 0;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    div {
      height: 1px;
      flex-grow: 1;
      background-color: rgba(0, 0, 0, 0.1);
    }
    span {
      border-bottom: 1px solid var(--outline);
    }
  }
  .inputForm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    flex-grow: 1;
    overflow: auto;
    button .editorWrapper {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    button.gray {
      font-weight: 500;
      color: var(--zinc-200);
    }
    label {
      width: 6.5rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
    }
    .inputForm-group-1280 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1280px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
      }
    }
    .inputForm-group-1536 {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      > .datePickerWrapper {
        display: flex;
        flex-direction: row;
        gap: 0 !important;

        > div {
          margin-left: 0.5rem;
        }

        .wave {
          padding: 0 0.5rem;
        }
        .rdrDateRangePickerWrapper {
          position: absolute !important;
          top: auto !important;
          bottom: 100% !important;
          margin-bottom: 10px !important;

          position: absolute;
          top: 0.25rem;
          left: 50%;
          -webkit-transform: translate(-50%, 0);
          -ms-transform: translate(-50%, 0);
          transform: translate(-50%, 0);
          outline: 1px solid var(--outline);
          border-radius: 0.25rem;
          z-index: 5100;
          background-color: var(--board);
          z-index: 4999;
          margin-top: 0.25rem;
          box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
        }
      }

      @media (min-width: 1536px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
      }
    }
    .inputForm-group-1536.withBtn {
      flex-direction: row;
      gap: 0.5rem;
      button {
        transition: none;
      }
      @media (min-width: 1536px) {
        gap: 2rem;
        button {
          margin-left: 0;
          width: fit-content;
        }
      }
    }
    .inputForm-group-1536.withBtn.inputColumnFour {
      input {
        padding: 0;
      }
    }
    .inputForm-row.labelInInput {
      display: flex;
      gap: 0.5rem;
      flex-grow: 1;
      > div {
        flex-grow: 1;
      }
    }
    .inputForm-row.labelInInput.noGrow {
      flex-grow: 0;
    }
    .inputForm-row.labelInInput.noGrow.title > div > div {
      flex-direction: column;
      align-items: flex-start;
    }
    .inputForm-row.labelOutInput,
    .inputForm-row.labelInInput > div > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      flex-grow: 1;
      input {
        flex-shrink: 1;
        height: 2.5rem;
        font-size: 0.875rem;
        padding: 0 0.5rem;
        &:disabled {
          background-color: var(--tonal);
          color: var(--text-disabled);
        }
      }
      > div.viewOnly {
        height: 2.5rem;
        font-size: 0.875rem;
        background-color: var(--tonal);
        color: var(--text-disabled);
        border-radius: 0.25rem;
        padding: 0 0.75rem;
        display: flex;
        align-items: center;
      }
      > div {
        flex-grow: 1;
        > ul {
          height: 2.5rem;
          li {
            display: flex;
            width: 100%;
            max-width: 100%;
            span {
              flex-grow: 1;
              width: 4rem;
            }
          }
        }
      }
    }
    .detailInfo-group {
      margin: 1rem 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      @media (min-width: 1536px) {
        grid-template-columns: repeat(3, 1fr);
      }
      .flex-col.detailInfo {
        flex-grow: 1;
        width: 100%;
        div {
          height: 2.5rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          font-size: 0.875rem;
        }
      }
    }

    .date-picker-btn {
      width: 2.5rem;
      height: 2.5rem;
      padding: 0 1rem;
      border-left: 1px solid var(--outline);
      background-color: var(--tonal);
      border-radius: 0;
      border-top-right-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;

      &:hover {
        background-color: var(--outline);
      }

      > span {
        font-size: 16px;
        color: var(--text-primary);
      }
    }
  }
  input {
    :disabled {
      background-color: #eeeeee;
    }
  }
  .required {
    &::after {
      content: '*';
      padding: 0 0.2rem;
      color: red;
    }
  }
  @media screen and (max-width: 1024px) {
  }

  @media screen and (max-width: 767px) {
    width: 100%;
    /* padding-top: 5rem; */
    height: fit-content;
    overflow: none;
  }
`;

const lastDayOfMonth = dayjs().endOf('month').toDate();

const SiteTransferEditorInputForm = ({
  type,
  contentKey,
  state,
  setState,
  isNewAdd,
  userInfoInputFormRef,
  onClickNewAdd,
  onClickSave,
  onClickDelete,
  onClickPreview,
  isSaveClicked,
  date,
  setDate,
  cDate,
  setCDate,
  editorData,
  setEditorData,
}: IUserInputForm) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const [pickerDateRange, setPickerDateRange] = useState([
    {
      startDate: dayjs(state.cDate).isValid() ? dayjs(state.cDate).toDate() : new Date(),
      endDate: dayjs(state.fDate).isValid() ? dayjs(state.fDate).toDate() : lastDayOfMonth,
      key: 'selection',
    },
  ]);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);

  useEffect(() => {
    if (type === 's') {
      setPickerDateRange([
        {
          startDate: dayjs(state.cDate).isValid() ? dayjs(state.cDate).toDate() : new Date(),
          endDate: dayjs(state.fDate).isValid() ? dayjs(state.fDate).toDate() : lastDayOfMonth,
          key: 'selection',
        },
      ]);
    }
  }, [state.cDate, state.fDate]);

  useEffect(() => {
    setEditorData(state[contentKey]);
  }, [state[contentKey]]);

  useEffect(() => {
    if (pickerDateRange[0].startDate) {
      setDate((prev) => ({ ...prev, start: dayjs(pickerDateRange[0].startDate).format('YYYY-MM-DD') }));
    }
    if (pickerDateRange[0].endDate) {
      setDate((prev) => ({ ...prev, end: dayjs(pickerDateRange[0].endDate).format('YYYY-MM-DD') }));
    }
  }, [pickerDateRange[0].startDate, pickerDateRange[0].endDate]);

  const onClickUseYn = () => {
    if (state.useYn === 'Y') setState((prev: any) => ({ ...prev, useYn: 'N' }));
    else setState((prev: any) => ({ ...prev, useYn: 'Y' }));
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  return (
    <Root>
      <div className='inputForm' ref={userInfoInputFormRef}>
        <div className='inputForm-row labelInInput noGrow title'>
          <Input
            className='required'
            label={t('제목')}
            type='text'
            id='cTitle'
            name='cTitle'
            state={state}
            setState={setState}
            disabled={!auth.updateAuth}
            getBorderStyle={isSaveClicked ? applyBorderStyle(state.cTitle, 'red', 'cTitle') : undefined}
          />
        </div>
        <div className='editorWrapper'>
          <TextEditor content={editorData} setContent={setEditorData} readOnly={false} />
        </div>
        <div className='inputForm-group-1536'>
          <div className='inputForm-row labelOutInput datePickerWrapper'>
            <label className='required' htmlFor='cDate'>
              {type === 's' ? t('게시 기간') : t('적용일자')}
            </label>
            {type === 's' ? (
              <>
                <div className='inputForm-col'>
                  <div className='flex-basic'>
                    <input id='startInput' type='text' value={date.start} onChange={(e) => onChangeRangeInput(e, 'start', setPickerDateRange, setDate)} />
                    <span className='wave'> ~ </span>
                    <input id='endInput' type='text' value={date.end} onChange={(e) => onChangeRangeInput(e, 'end', setPickerDateRange, setDate)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={pickerDateRange} setState={setPickerDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
                <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                  <span className='material-symbols-rounded'>calendar_month</span>
                </button>
              </>
            ) : (
              <DatePicker startDate={cDate} setDate={setCDate} popperPlacement='bottom' isSaveClicked={isSaveClicked} />
            )}
          </div>
          <div className='inputForm-row labelOutInput useYn'>
            <label htmlFor='useYn'>{t(type === 's' ? `사용유무` : `상황판 적용`)}</label>
            <ClickableContainer className='flex-start' button={<TableLamp use={state.useYn === 'Y'} />} onClick={onClickUseYn} />
          </div>
        </div>
        <div className='inputForm-row labelInInput noGrow'>
          <Input label={t('비고')} type='text' id='bigo' name='bigo' state={state} setState={setState} disabled={!auth.updateAuth} maxLength={100} />
        </div>
        {!isNewAdd ? (
          <div className='detailInfo-group'>
            <div className='flex-col detailInfo'>
              <label htmlFor='createDate'>{t('등록일자')}</label>
              <div>{state?.wDate}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='createUser'>{t('등록자')}</label>
              <div>{state.writer}</div>
            </div>

            <div className='flex-col detailInfo'>
              <label htmlFor='updateDate'>{t('수정일자')}</label>
              <div>{state.eDate}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='updateUser'>{t('수정자')}</label>
              <div>{state.editor}</div>
            </div>
          </div>
        ) : undefined}
      </div>
      <ButtonsWrapper>
        {!isNewAdd && auth.deleteAuth && <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>}
        {!isNewAdd && <BtnGreen onClick={onClickPreview}>{t('미리보기')}</BtnGreen>}
        {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
      </ButtonsWrapper>
    </Root>
  );
};

export default SiteTransferEditorInputForm;
